// src/components/admin/contact/ContactTable.jsx
import React from 'react';
import { FiEye, FiTrash2, FiMail, FiPhone, FiCalendar } from 'react-icons/fi';
import Button from '../../common/Button';
import { formatDate } from '../../../utils/formatters';

const ContactTable = ({ 
  contacts, 
  onViewDetail, 
  onStatusUpdate, 
  onDelete, 
  loading 
}) => {
  const getStatusBadge = (status) => {
    const statusConfig = {
      new: { color: 'bg-red-100 text-red-800', label: 'Baru' },
      read: { color: 'bg-blue-100 text-blue-800', label: 'Dibaca' },
      replied: { color: 'bg-green-100 text-green-800', label: 'Dibalas' },
      closed: { color: 'bg-gray-100 text-gray-800', label: 'Ditutup' }
    };

    const config = statusConfig[status] || statusConfig.new;
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${config.color}`}>
        {config.label}
      </span>
    );
  };

  const getStatusOptions = (currentStatus) => {
    const options = [
      { value: 'new', label: 'Baru' },
      { value: 'read', label: 'Dibaca' },
      { value: 'replied', label: 'Dibalas' },
      { value: 'closed', label: 'Ditutup' }
    ];
    return options.filter(option => option.value !== currentStatus);
  };

  if (contacts.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-sm border p-8 text-center">
        <FiMail className="mx-auto h-12 w-12 text-gray-400 mb-4" />
        <p className="text-gray-500">Belum ada pesan kontak</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Pengirim
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Subject
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Tanggal
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Aksi
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {contacts.map((contact) => (
              <tr key={contact.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap">
                  <div>
                    <div className="text-sm font-medium text-gray-900">
                      {contact.name}
                    </div>
                    <div className="text-sm text-gray-500 flex items-center gap-1">
                      <FiMail className="h-3 w-3" />
                      {contact.email}
                    </div>
                    {contact.phone && (
                      <div className="text-sm text-gray-500 flex items-center gap-1">
                        <FiPhone className="h-3 w-3" />
                        {contact.phone}
                      </div>
                    )}
                  </div>
                </td>
                <td className="px-6 py-4">
                  <div className="text-sm text-gray-900">
                    {contact.subject}
                  </div>
                  <div className="text-sm text-gray-500 truncate max-w-xs">
                    {contact.message}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex flex-col gap-2">
                    {getStatusBadge(contact.status)}
                    <select
                      value={contact.status}
                      onChange={(e) => onStatusUpdate(contact.id, e.target.value)}
                      className="text-xs border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    >
                      <option value={contact.status}>
                        {getStatusOptions(contact.status).find(opt => opt.value === contact.status)?.label || 'Current'}
                      </option>
                      {getStatusOptions(contact.status).map(option => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  <div className="flex items-center gap-1">
                    <FiCalendar className="h-3 w-3" />
                    {formatDate(contact.created_at)}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                  <div className="flex items-center gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => onViewDetail(contact)}
                    >
                      <FiEye className="h-4 w-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => onDelete(contact.id)}
                      className="text-red-600 hover:text-red-700"
                    >
                      <FiTrash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ContactTable;